'use client';

import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import {
  ACTIVITY_BY_EVENT_QK,
  ACTIVITY_INFINITE_PAGE_SIZE,
  TRADE_HISTORY_QK,
} from '@/lib/api/history/hooks';
import type {
  ActivityItem,
  ActivityPageResponse,
  TradeHistory,
  TradeHistoryPageResponse,
} from '@/lib/api/history/models';
import { mapPlayerLook } from '@/lib/api/rewards-hub/mappers';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type { ActivityUpdateMessage, SubscribeActivityMessage } from '@/lib/types/websocket';

export type ActivityUpdateHandler = (update: ActivityUpdateMessage) => void;

export function useActivityHistoryWebSocket(
  eventId: string,
  onUpdate?: ActivityUpdateHandler,
  options?: {
    autoConnect?: boolean;
  },
) {
  const { user } = useAuthContext();

  // User id comes from the server session (/api/user/me) — no JWT decode needed.
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const onUpdateRef = useRef<ActivityUpdateHandler | undefined>(undefined);
  const eventIdRef = useRef<string>(eventId);

  const { autoConnect: autoConnectOption = true } = options || {};

  // Keep latest handler in ref to avoid re-creating connect() on each render
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Keep eventId in ref for subscription
  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);

  const handleActivityUpdate = useCallback(
    (msg: ActivityUpdateMessage) => {
      const payload = msg.d;
      const items = payload?.data;

      if (!Array.isArray(items) || items.length === 0) {
        console.warn('ActivityUpdate: empty or malformed batch', msg);
        return;
      }

      // Call custom handler if provided
      onUpdateRef.current?.(msg);

      const batchEventId = payload.eventId ?? eventIdRef.current;

      for (const item of items) {
        // Per-user trade history live update — push a row for each of the
        // user's own matches into [TRADE_HISTORY_QK, outcomeId].
        if (userId && item.u === userId) {
          const position: 'Yes' | 'No' = item.p === 'Yes' ? 'Yes' : 'No';

          // Optimistic per-match row for live History rendering.
          // `assetId` / UUID `buyerId` arrive when useOrderHistoryWebSocket
          // invalidates TRADE_HISTORY_QK on FILLED → REST refetch overwrites.
          const newTrade: TradeHistory = {
            id: item.tid,
            buyerId: '',
            sellerId: item.si,
            contractId: '',
            dealId: item.tid,
            eventId: batchEventId,
            eventOutcomeId: item.oi,
            assetId: '',
            quantity: item.q,
            price: item.pr,
            fee: 0,
            revenue: 0,
            matchTime: item.x,
            position,
          };

          queryClient.setQueryData<TradeHistoryPageResponse>(
            [TRADE_HISTORY_QK, item.oi],
            (oldData) => {
              if (!oldData) {
                return {
                  content: [newTrade],
                  totalElements: 1,
                  totalPages: 1,
                  size: 1,
                  number: 0,
                  numberOfElements: 1,
                  first: true,
                  last: true,
                  empty: false,
                };
              }
              if (oldData.content.some((trade) => trade.id === newTrade.id)) {
                return oldData;
              }
              return {
                ...oldData,
                content: [newTrade, ...oldData.content],
                totalElements: oldData.totalElements + 1,
                numberOfElements: oldData.numberOfElements + 1,
              };
            },
          );

          queryClient.setQueryData<boolean>(
            [TRADE_HISTORY_QK, 'has-data', batchEventId, item.oi],
            true,
          );
        }

        const newActivityItem: ActivityItem = {
          transactionId: item.tid,
          userId: item.u,
          username: item.n,
          outcomeId: item.oi,
          outcomeName: item.on,
          position: item.p,
          side: item.si,
          shares: item.q,
          executedAt: item.x,
          look: mapPlayerLook(item.lk),
        };

        queryClient.setQueryData<InfiniteData<ActivityPageResponse>>(
          [ACTIVITY_BY_EVENT_QK, 'infinite', batchEventId, ACTIVITY_INFINITE_PAGE_SIZE],
          (oldData) => {
            if (!oldData?.pages.length) return oldData;

            const exists = oldData.pages[0].items.some(
              (existing) => existing.transactionId === newActivityItem.transactionId,
            );
            if (exists) return oldData;

            return {
              ...oldData,
              pages: [
                { ...oldData.pages[0], items: [newActivityItem, ...oldData.pages[0].items] },
                ...oldData.pages.slice(1),
              ],
            };
          },
        );
      }
    },
    [queryClient, userId],
  );

  const { send, ...activityHistorySocket } = useWebSocket({
    url: WS_ENDPOINTS.activityHistory,
    autoConnect: autoConnectOption && Boolean(eventId),
    reconnect: true,
    onOpen: () => {
      // Send SUBSCRIBE message with eventId on connect
      if (eventIdRef.current) {
        const subscribeMessage: SubscribeActivityMessage = {
          t: 'SUBSCRIBE',
          d: { eventId: eventIdRef.current },
        };
        send(subscribeMessage);
      }
    },
    onActivityUpdate: handleActivityUpdate,
    onErrorMessage: (errorMsg) => {
      console.error('Activity History WebSocket error:', errorMsg);
    },
    onClose: (_event) => {},
  });

  // No need for additional useEffect - useWebSocket already manages connection via autoConnect parameter

  return {
    ...activityHistorySocket,
    send,
  };
}
